import React, { useState } from 'react';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from './ui/dropdown-menu';
import { useToast } from '../hooks/use-toast';
import { Download, FileSpreadsheet, FileJson, FileText, Loader2, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ExportButton = ({ 
  dataType = 'incidents', 
  label = 'Export',
  variant = 'outline',
  size = 'sm',
  showIcon = true,
  className = ''
}) => {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);

  const exportConfigs = {
    incidents: {
      label: 'Incidents',
      csv: '/api/exports/incidents/csv',
      json: '/api/exports/incidents/json'
    },
    capa: {
      label: 'CAPA Records',
      csv: '/api/exports/capa/csv',
      json: '/api/exports/capa/json'
    },
    checklists: {
      label: 'Checklist Submissions',
      csv: '/api/exports/checklists/csv',
      json: null
    },
    hazards: {
      label: 'Hazards',
      csv: '/api/exports/hazards/csv',
      json: null
    },
    'ai-scans': {
      label: 'AI Scans',
      csv: '/api/exports/ai-scans/csv',
      json: null
    },
    training: {
      label: 'Training Records',
      csv: '/api/exports/training/csv',
      json: null
    },
    comprehensive: {
      label: 'Comprehensive Report',
      csv: null,
      json: '/api/exports/comprehensive-report'
    }
  };

  const handleExport = async (format) => {
    const config = exportConfigs[dataType];
    if (!config) {
      toast({ title: 'Error', description: 'Unknown export type', variant: 'destructive' });
      return;
    }

    const endpoint = format === 'csv' ? config.csv : config.json;
    if (!endpoint) {
      toast({ title: 'Error', description: `${format.toUpperCase()} export not available for ${config.label}`, variant: 'destructive' });
      return;
    }

    setExporting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${dataType}_export_${new Date().toISOString().split('T')[0]}.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({ 
        title: 'Export Complete', 
        description: `${config.label} exported as ${format.toUpperCase()}`
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({ 
        title: 'Export Failed', 
        description: 'Unable to download the export file', 
        variant: 'destructive' 
      });
    } finally {
      setExporting(false);
    }
  };

  const config = exportConfigs[dataType];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} className={className} disabled={exporting} data-testid={`export-btn-${dataType}`}>
          {exporting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : showIcon ? (
            <Download className="w-4 h-4" />
          ) : null}
          {showIcon && <span className="ml-2">{label}</span>}
          {!showIcon && label}
          <ChevronDown className="w-3 h-3 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Export {config?.label || dataType}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {config?.csv && (
          <DropdownMenuItem onClick={() => handleExport('csv')} className="cursor-pointer">
            <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
            Download CSV
          </DropdownMenuItem>
        )}
        {config?.json && (
          <DropdownMenuItem onClick={() => handleExport('json')} className="cursor-pointer">
            <FileJson className="w-4 h-4 mr-2 text-blue-600" />
            Download JSON
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Quick Export Section Component for Dashboard
export const QuickExportSection = () => {
  const { toast } = useToast();
  const [generating, setGenerating] = useState(false);

  const downloadComprehensiveReport = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/api/exports/comprehensive-report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to generate report');
      
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wsafe_comprehensive_report_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({ title: 'Report Generated', description: 'Comprehensive report downloaded successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to generate report', variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="quick-export-section">
      <ExportButton dataType="incidents" label="Incidents" />
      <ExportButton dataType="capa" label="CAPA" />
      <ExportButton dataType="hazards" label="Hazards" />
      <Button 
        variant="outline" 
        size="sm" 
        onClick={downloadComprehensiveReport}
        disabled={generating}
        data-testid="export-comprehensive-btn"
      >
        {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
        Full Report
      </Button>
    </div>
  );
};

export default ExportButton;
